/**
 * The API client.
 *
 * Session model: the refresh token lives in an HttpOnly cookie scoped to the
 * base domain, so it is invisible to JavaScript and shared by every space
 * subdomain. The access token is a short-lived PASETO held **in memory only** —
 * nothing about the session is persisted to localStorage, so an XSS has no
 * stored credential to steal and a reload simply re-mints an access token from
 * the cookie.
 */

const API_BASE: string = import.meta.env.VITE_API_BASE || ''

let accessToken: string | null = null

export function setAccessToken(token: string | null): void {
  accessToken = token
}

export function hasAccessToken(): boolean {
  return accessToken !== null
}

/** Thrown for any non-2xx response, carrying the backend's error code. */
export class ApiError extends Error {
  readonly status: number
  readonly code: string

  constructor(status: number, code: string, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE'
  body?: unknown
  /** Set false to opt out of the automatic refresh-and-retry on 401. */
  retryOn401?: boolean
  signal?: AbortSignal
}

/**
 * A single in-flight refresh shared by all callers.
 *
 * This matters more than it looks: refresh rotates the token and each rotation
 * invalidates the previous one. If five parallel requests each triggered their
 * own refresh, four would present a token that a sibling had already consumed
 * and the user would be logged out at random. Single-flight makes the concurrent
 * case behave like the sequential one.
 */
let refreshInFlight: Promise<boolean> | null = null

export function refreshSession(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = doRefresh().finally(() => {
      refreshInFlight = null
    })
  }
  return refreshInFlight
}

async function doRefresh(): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/api/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    })
    if (!res.ok) {
      accessToken = null
      return false
    }
    const payload = (await res.json()) as { data?: { accessToken?: string } }
    const token = payload?.data?.accessToken
    if (typeof token !== 'string' || token === '') {
      accessToken = null
      return false
    }
    accessToken = token
    return true
  } catch {
    accessToken = null
    return false
  }
}

/** Drop the session server-side and locally. */
export async function endSession(): Promise<void> {
  try {
    await fetch(`${API_BASE}/api/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    })
  } catch {
    // A failed logout must not trap the user: the local token is cleared below
    // and the server-side token expires on its own.
  }
  accessToken = null
}

function defaultMessage(status: number): string {
  if (status === 401) return 'Your session has expired. Please sign in again.'
  if (status === 403) return 'You do not have permission to do that.'
  if (status === 404) return 'Not found.'
  if (status === 409) return 'That conflicts with something that already exists.'
  if (status === 429) return 'Too many requests. Please slow down.'
  if (status >= 500) return 'Something went wrong on our side.'
  return `Request failed (${status}).`
}

/**
 * Perform a request against the dashboard API and unwrap the `{data: ...}`
 * envelope. Any `/api/v1/**` (OpenAI-shaped) endpoints are not used by this app.
 */
export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, retryOn401 = true, signal } = options

  const headers: Record<string, string> = { Accept: 'application/json' }
  if (body !== undefined) headers['Content-Type'] = 'application/json'
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    // Same-origin through Caddy, but explicit so the cookie still rides along if
    // the API is ever served from a different origin.
    credentials: 'include',
    signal,
    body: body === undefined ? undefined : JSON.stringify(body),
  })

  // A 401 means the 15-minute access token aged out. Refresh once and replay;
  // never on the auth endpoints themselves, which would recurse.
  if (res.status === 401 && retryOn401 && !path.startsWith('/api/auth/')) {
    if (await refreshSession()) {
      return api<T>(path, { ...options, retryOn401: false })
    }
  }

  const text = await res.text()
  let payload: unknown = null
  if (text) {
    try {
      payload = JSON.parse(text)
    } catch {
      payload = null
    }
  }

  if (!res.ok) {
    const envelope = payload as { error?: { code?: string; message?: string } } | null
    const error = envelope?.error
    throw new ApiError(
      res.status,
      typeof error?.code === 'string' ? error.code : `http_${res.status}`,
      typeof error?.message === 'string' ? error.message : defaultMessage(res.status),
    )
  }

  if (payload && typeof payload === 'object' && 'data' in payload) {
    return (payload as { data: T }).data
  }
  return payload as T
}

/* ---------------------------------------------------------------------------
   The OAuth fragment
--------------------------------------------------------------------------- */

export interface LoginFragment {
  accessToken?: string
  expiresAt?: string
  error?: string
}

/**
 * Read the `#googleLogin&...` fragment the OAuth callback redirects with.
 *
 * Returns null when there is no fragment, so the caller can distinguish "no
 * login just happened" from "login failed".
 */
export function readLoginFragment(): LoginFragment | null {
  const raw = window.location.hash.replace(/^#/, '')
  if (!raw.startsWith('googleLogin')) return null
  const params = new URLSearchParams(raw.slice('googleLogin'.length).replace(/^&/, ''))
  return {
    accessToken: params.get('accessToken') ?? undefined,
    expiresAt: params.get('expiresAt') ?? undefined,
    error: params.get('error') ?? undefined,
  }
}

/**
 * Strip the fragment from the URL and from history.
 *
 * The access token is short-lived, but there is no reason to leave it sitting in
 * the address bar or in the back/forward history.
 */
export function clearLoginFragment(): void {
  if (!window.location.hash) return
  window.history.replaceState(
    null,
    '',
    window.location.pathname + window.location.search,
  )
}

/** Map the callback's terse error codes to something a person can act on. */
export function describeLoginError(code: string): string {
  switch (code) {
    case 'bad_state':
    case 'missing_params':
      return 'That sign-in attempt expired. Please try again.'
    case 'token_exchange':
    case 'id_token':
      return 'Google could not confirm the sign-in. Please try again.'
    case 'account':
      return 'We could not set up your account. Please try again.'
    case 'issue_access':
    case 'issue_refresh':
      return 'Sign-in succeeded but the session could not be created.'
    case 'access_denied':
      return 'Sign-in was cancelled.'
    default:
      return 'Sign-in failed. Please try again.'
  }
}
